import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  IconButton,
  Button,
  Tabs,
  Tab,
  Tooltip,
  Stack,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  TrendingUp,
  Devices,
  SimCard,
  People,
  Warning,
  Error as ErrorIcon,
  CheckCircle,
  Refresh,
  Sync,
  Visibility,
  VisibilityOff,
  OpenInNew,
  Info,
  AccountBalanceWallet,
} from '@mui/icons-material';
import { useApi, useAsyncOperation } from '../../hooks/useApi';
import { apiService } from '../../services/api';
import { DashboardKPIs } from '../../types';
import { formatCurrency, formatDate } from '../../utils/helpers';
import { useNavigate } from 'react-router-dom';

interface StatCardProps {
  title: string;
  value: number;
  subtitle?: string;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  trend?: { value: number; isPositive: boolean };
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, icon, color, trend }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box
          sx={{
            backgroundColor: `${color}.main`,
            color: 'white',
            borderRadius: 2,
            p: 1.5,
            mr: 2,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {value.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
        </Box>
      </Box>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {subtitle}
        </Typography>
      )}
      {trend && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TrendingUp
            sx={{
              fontSize: 16,
              color: trend.isPositive ? 'success.main' : 'error.main',
              transform: trend.isPositive ? 'none' : 'rotate(180deg)',
              mr: 0.5,
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: trend.isPositive ? 'success.main' : 'error.main', fontWeight: 500 }}
          >
            {trend.isPositive ? '+' : ''}{trend.value}%
          </Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

const severityConfig: Record<string, { color: string; bgColor: string; icon: React.ReactNode; label: string }> = {
  critical: { color: 'error.main', bgColor: 'error.50', icon: <ErrorIcon color="error" fontSize="small" />, label: 'Crítica' },
  high: { color: 'warning.main', bgColor: 'warning.50', icon: <Warning color="warning" fontSize="small" />, label: 'Alta' },
  medium: { color: 'info.main', bgColor: 'info.50', icon: <Info color="info" fontSize="small" />, label: 'Media' },
  info: { color: 'success.main', bgColor: 'success.50', icon: <CheckCircle color="success" fontSize="small" />, label: 'Info' },
};

const typeLabels: Record<string, string> = {
  sim_high_usage: 'SIM Alto Uso',
  expired_sim_active: 'SIM Activa Expirada',
  accident: 'Accidente',
  device_no_report_48h: 'Sin Reporte 48h',
  renewal_7_days: 'Vence 7 días',
  device_offline_24h: 'Offline 24h',
  sim_disabled_active_client: 'SIM Desactivada',
  technician_high_balance: 'Balance Técnico',
  renewal_30_days: 'Vence 30 días',
  device_no_expiration: 'Sin Expiración',
  installation_pending_3d: 'Instalación Pendiente',
  sim_disconnected: 'SIM Desconectada',
  installation_completed: 'Instalación OK',
  renewal_completed: 'Renovación OK',
  sync_failed: 'Sync Fallido',
  simbase_low_balance: 'Saldo Bajo Simbase',
  app_purchase_problem: 'Compra fallida (app)',
  app_purchase_intent: 'Quiere comprar (app)',
};

export const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const {
    data: stats,
    loading: statsLoading,
    error: statsError,
    execute: fetchStats,
  } = useApi<DashboardKPIs>(() => apiService.getDashboardKPIs());

  const { execute: syncAllData, loading: syncing } = useAsyncOperation();

  // Simbase finance state
  const [simbaseFinance, setSimbaseFinance] = useState<any>(null);
  const [financeLoading, setFinanceLoading] = useState(true);

  const fetchSimbaseFinance = useCallback(async (fresh = false) => {
    setFinanceLoading(true);
    try {
      const res = await apiService.getSimbaseBalance(fresh);
      const data = (res as any)?.data || res;
      setSimbaseFinance(data);
    } catch (e) {
      console.error('Error fetching Simbase finance:', e);
    }
    setFinanceLoading(false);
  }, []);

  useEffect(() => {
    fetchSimbaseFinance();
  }, [fetchSimbaseFinance]);

  // Notifications state
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notifLoading, setNotifLoading] = useState(true);
  const [notifCounts, setNotifCounts] = useState<any>({});
  const [severityFilter, setSeverityFilter] = useState('all');
  // El segundo eje del filtro: de qué trata la alerta. La severidad dice cuánto corre, no de
  // qué es; quien atiende ventas no tiene por qué leer las alertas de chips, y al revés.
  const [categoriaFiltro, setCategoriaFiltro] = useState('all');
  const [categorias, setCategorias] = useState<any[]>([]);
  const [porCategoria, setPorCategoria] = useState<any>({});
  const [notifPage, setNotifPage] = useState(1);
  const [notifTotal, setNotifTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async (page = 1, severity?: string, categoria?: string) => {
    setNotifLoading(true);
    try {
      const params: any = { limit: 15, page };
      if (severity && severity !== 'all') params.severity = severity;
      if (categoria && categoria !== 'all') params.categoria = categoria;
      const res = await apiService.getAlerts(params);
      const data = (res as any)?.data || res;
      setNotifications(data.notifications || []);
      setNotifCounts(data.bySeverity || {});
      setPorCategoria(data.porCategoria || {});
      if (data.categorias?.length) setCategorias(data.categorias);
      setNotifTotal(data.pagination?.total || 0);
      setUnreadCount(data.unreadCount || 0);
    } catch (e) {
      console.error('Error fetching notifications:', e);
    }
    setNotifLoading(false);
  }, []);

  useEffect(() => {
    fetchNotifications(1);
  }, [fetchNotifications]);

  const handleSeverityChange = (_: any, newVal: string) => {
    setSeverityFilter(newVal);
    setNotifPage(1);
    fetchNotifications(1, newVal, categoriaFiltro);
  };

  const handleCategoriaChange = (clave: string) => {
    const nueva = categoriaFiltro === clave ? 'all' : clave;
    setCategoriaFiltro(nueva);
    setNotifPage(1);
    fetchNotifications(1, severityFilter, nueva);
  };

  const handleMarkRead = async (id: string) => {
    try {
      await apiService.markNotificationRead(id);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { /* silent */ }
  };

  const handleDismiss = async (id: string) => {
    try {
      await apiService.dismissNotification(id, 'false_positive');
      setNotifications(prev => prev.filter(n => n._id !== id));
      setNotifTotal(prev => prev - 1);
    } catch (e) { /* silent */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await apiService.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) { /* silent */ }
  };

  const handleRefresh = () => {
    fetchStats();
    fetchNotifications(notifPage, severityFilter, categoriaFiltro);
    fetchSimbaseFinance();
  };

  const handleSync = async () => {
    try {
      await syncAllData(() => apiService.syncAllData());
      fetchStats();
      fetchNotifications(1);
    } catch (error) {
      console.error('Error durante la sincronización:', error);
    }
  };

  const navigateToEntity = (n: any) => {
    if (!n.read) handleMarkRead(n._id);
    if (n.entityType === 'device' && n.entityId) navigate(`/devices/${n.entityId}`);
    else if (n.entityType === 'simcard' && n.entityId) navigate(`/simcards/${n.entityId}`);
    else if (n.entityType === 'technician' && n.entityId) navigate(`/technicians/${n.entityId}/balance`);
  };

  const handleLoadMore = () => {
    const next = notifPage + 1;
    setNotifPage(next);
    fetchNotifications(next, severityFilter, categoriaFiltro);
  };

  if (statsError) {
    return (
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Dashboard Administrativo
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          Error cargando el dashboard: {statsError}
          <Button onClick={handleRefresh} sx={{ ml: 2 }}>Reintentar</Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Resumen general del sistema
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Actualizar">
            <IconButton onClick={handleRefresh} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={18} /> : <Sync />}
            onClick={handleSync}
            disabled={syncing}
            size="small"
          >
            {syncing ? 'Sincronizando...' : 'Sync'}
          </Button>
        </Stack>
      </Box>

      {statsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* KPI Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <StatCard title="Dispositivos" value={stats?.devices?.total || 0} icon={<Devices />} color="primary" subtitle={`${stats?.devices?.online || 0} online`} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <StatCard title="Clientes" value={stats?.clients?.total || 0} icon={<People />} color="success" subtitle={`${stats?.clients?.active || 0} activos`} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <StatCard title="SIM Cards" value={stats?.sims?.total || 0} icon={<SimCard />} color="info" subtitle={`${stats?.sims?.active || 0} activas`} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <StatCard title="Renovaciones" value={stats?.renewals?.dueThisMonth || 0} icon={<Warning />} color="warning" subtitle={`${stats?.renewals?.overdue || 0} vencidas`} />
            </Grid>
          </Grid>

          {/* Revenue & Renewals */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Ingresos del Mes</Typography>
                  <Typography variant="h3" color="success.main" sx={{ fontWeight: 600 }}>
                    {formatCurrency(stats?.revenue?.thisMonth || 0)}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">
                      {stats?.revenue?.installations || 0} instalaciones
                    </Typography>
                    {(stats?.revenue?.byMethod?.cash || 0) > 0 && (
                      <Chip label={`Efectivo: ${formatCurrency(stats?.revenue?.byMethod?.cash || 0)}`} color="warning" size="small" />
                    )}
                    {(stats?.revenue?.byMethod?.transfer || 0) > 0 && (
                      <Chip label={`Transf: ${formatCurrency(stats?.revenue?.byMethod?.transfer || 0)}`} color="info" size="small" />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Renovaciones Pendientes</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h3" color="warning.main" sx={{ fontWeight: 600 }}>
                      {stats?.renewals?.dueThisMonth || 0}
                    </Typography>
                    {(stats?.renewals?.overdue || 0) > 0 && (
                      <Chip label={`${stats?.renewals?.overdue} vencidas`} color="error" size="small" />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Requieren atención inmediata
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Simbase Finance */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccountBalanceWallet color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>Saldo Simbase</Typography>
                    </Box>
                    <Tooltip title="Actualizar saldo">
                      <IconButton size="small" onClick={() => fetchSimbaseFinance(true)} disabled={financeLoading}>
                        {financeLoading ? <CircularProgress size={18} /> : <Refresh fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {simbaseFinance ? (
                    <>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 600,
                          color: simbaseFinance.balance < 30 ? 'error.main' : simbaseFinance.balance < 50 ? 'warning.main' : 'success.main',
                        }}
                      >
                        ${simbaseFinance.balance?.toFixed(2)} <Typography component="span" variant="h6" color="text.secondary">{simbaseFinance.currency}</Typography>
                      </Typography>
                      <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {simbaseFinance.autoTopUpEnabled ? (
                          <Chip
                            label={`Auto-recarga: $${simbaseFinance.autoTopUpAmount} al llegar a $${simbaseFinance.autoTopUpThreshold}`}
                            color="success"
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <Chip label="Auto-recarga desactivada" color="error" size="small" variant="outlined" />
                        )}
                      </Box>
                      {simbaseFinance.lastFetchedAt && (
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                          Actualizado: {new Date(simbaseFinance.lastFetchedAt).toLocaleString('es-DO')}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography color="text.secondary">No disponible</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <SimCard color="info" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Gasto SIM del Mes</Typography>
                  </Box>
                  {simbaseFinance?.totalCost != null ? (
                    <>
                      <Typography variant="h3" color="info.main" sx={{ fontWeight: 600 }}>
                        ${simbaseFinance.totalCost.toFixed(2)} <Typography component="span" variant="h6" color="text.secondary">USD</Typography>
                      </Typography>
                      <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={`Data: $${simbaseFinance.dataCost?.toFixed(2) || '0'}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          label={`Line rental: $${simbaseFinance.lineRentalCost?.toFixed(2) || '0'}`}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                        <Chip
                          label={`${simbaseFinance.activeSimCount || 0} SIMs con uso`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      {simbaseFinance.totalDataBytes > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Consumo total: {(simbaseFinance.totalDataBytes / 1000000).toFixed(1)} MB
                        </Typography>
                      )}
                      {simbaseFinance.month && (
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                          Mes: {simbaseFinance.month}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography color="text.secondary">Sin datos de gasto este mes</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Notifications Section */}
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Alertas Importantes
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {unreadCount > 0 ? `${unreadCount} sin leer de ${notifTotal} total` : `${notifTotal} alertas`}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                {unreadCount > 0 && (
                  <Button size="small" variant="outlined" onClick={handleMarkAllRead}>
                    Marcar todas leídas
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={() => { apiService.generateNotifications().then(() => fetchNotifications(1, severityFilter, categoriaFiltro)); }}
                >
                  Regenerar
                </Button>
              </Stack>
            </Box>

            {/* De qué trata: el filtro que cada quien usa para ver solo lo suyo */}
            {categorias.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  ¿De qué trata?
                </Typography>
                <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                  <Chip
                    size="small"
                    label="Todo"
                    color={categoriaFiltro === 'all' ? 'primary' : 'default'}
                    variant={categoriaFiltro === 'all' ? 'filled' : 'outlined'}
                    onClick={() => handleCategoriaChange('all')}
                  />
                  {categorias.map((c: any) => {
                    const cuenta = porCategoria[c.clave];
                    if (!cuenta?.total) return null;
                    const activa = categoriaFiltro === c.clave;
                    return (
                      <Tooltip key={c.clave} title={c.descripcion}>
                        <Chip
                          size="small"
                          label={`${c.etiqueta} ${cuenta.total}${cuenta.sinLeer ? ` · ${cuenta.sinLeer} sin leer` : ''}`}
                          color={activa ? (c.clave === 'emergencia' ? 'error' : 'primary') : 'default'}
                          variant={activa ? 'filled' : 'outlined'}
                          onClick={() => handleCategoriaChange(c.clave)}
                          sx={c.clave === 'emergencia' && !activa ? { borderColor: 'error.main', color: 'error.main' } : undefined}
                        />
                      </Tooltip>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {/* Severity Tabs */}
            <Tabs
              value={severityFilter}
              onChange={handleSeverityChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label={`Todas (${notifTotal})`} value="all" />
              <Tab
                label={`Críticas (${notifCounts.critical || 0})`}
                value="critical"
                sx={{ color: 'error.main' }}
              />
              <Tab
                label={`Altas (${notifCounts.high || 0})`}
                value="high"
                sx={{ color: 'warning.main' }}
              />
              <Tab
                label={`Medias (${notifCounts.medium || 0})`}
                value="medium"
                sx={{ color: 'info.main' }}
              />
              <Tab
                label={`Info (${notifCounts.info || 0})`}
                value="info"
              />
            </Tabs>

            {/* Notification List */}
            {notifLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : notifications.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No hay alertas
                {categoriaFiltro !== 'all' ? ` de ${(categorias.find((c: any) => c.clave === categoriaFiltro)?.etiqueta || categoriaFiltro).toLowerCase()}` : ''}
                {severityFilter !== 'all' ? ` con severidad "${severityFilter}"` : ''}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {notifications.map((n: any) => {
                  const cfg = severityConfig[n.severity] || severityConfig.info;
                  return (
                    <Box
                      key={n._id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1.5,
                        border: '1px solid',
                        borderColor: n.read ? 'divider' : `${cfg.color}`,
                        borderRadius: 2,
                        backgroundColor: n.read ? 'transparent' : cfg.bgColor,
                        opacity: n.read ? 0.75 : 1,
                        cursor: n.entityId ? 'pointer' : 'default',
                        transition: 'all 0.2s',
                        '&:hover': { boxShadow: n.entityId ? 1 : 0 },
                      }}
                      onClick={() => n.entityId && navigateToEntity(n)}
                    >
                      <Box sx={{ mr: 1.5, display: 'flex' }}>
                        {cfg.icon}
                      </Box>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: n.read ? 400 : 600 }}>
                            {n.title}
                          </Typography>
                          {n.categoria && (
                            <Chip
                              label={categorias.find((c: any) => c.clave === n.categoria)?.etiqueta || n.categoria}
                              size="small"
                              color={n.categoria === 'emergencia' ? 'error' : 'default'}
                              onClick={(e) => { e.stopPropagation(); handleCategoriaChange(n.categoria); }}
                              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                            />
                          )}
                          <Chip label={typeLabels[n.type] || n.type} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                          {n.description}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                          {formatDate(n.createdAt, 'dd/MM/yyyy HH:mm')}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5} sx={{ ml: 1, flexShrink: 0 }}>
                        {n.entityId && (
                          <Tooltip title="Ver detalle">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigateToEntity(n); }}>
                              <OpenInNew fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!n.read && (
                          <Tooltip title="Marcar como leída">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMarkRead(n._id); }}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Ocultar (falso positivo)">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDismiss(n._id); }} color="default">
                            <VisibilityOff fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  );
                })}

                {notifTotal > notifications.length && (
                  <Button
                    variant="text"
                    onClick={handleLoadMore}
                    sx={{ mt: 1, alignSelf: 'center' }}
                  >
                    Cargar más ({notifTotal - notifications.length} restantes)
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default AdminDashboard;
